import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Users, Shield, ShieldOff, 
  UserPlus, RefreshCw, Copy, Check, Search, Mail, Calendar, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  
  // Email lookup state
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [searchError, setSearchError] = useState('');
  
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Fetch current user info and admin list in parallel
      const [userInfo, adminList] = await Promise.all([
        adminApi.getSetupCheck(token),
        adminApi.getAdminUsers(token)
      ]);
      
      setCurrentUserInfo(userInfo);
      setAdmins(adminList);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.message.includes('403')) {
        navigate('/admin');
      }
      toast({
        title: "Error",
        description: err.message || "Failed to load admin users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const handleEmailLookup = async (e) => {
    e.preventDefault();
    
    if (!searchEmail.trim()) {
      setSearchError('Please enter an email address');
      return;
    }

    try {
      setSearching(true);
      setSearchError('');
      setFoundUser(null);
      
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      const user = await adminApi.lookupUserByEmail(searchEmail.trim(), token);
      setFoundUser(user);
    } catch (err) {
      if (err.message.includes('404')) {
        setSearchError('No user found with this email. Make sure they have signed in at least once.');
      } else {
        setSearchError(err.message || 'Failed to look up user');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleElevateToAdmin = async () => {
    if (!foundUser) return;

    try {
      setAdding(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.createAdminUser(foundUser.user_id, token);
      
      toast({
        title: "Success",
        description: `${foundUser.email} is now an administrator`,
      });
      
      // Clear search and refresh
      setSearchEmail('');
      setFoundUser(null);
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to add admin user",
        variant: "destructive"
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAdmin = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to remove admin access for ${email || 'this user'}?`)) {
      return;
    }

    try {
      setRemoving(userId);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.removeAdminUser(userId, token);
      
      toast({
        title: "Success",
        description: "Admin access removed successfully",
      });
      
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to remove admin user",
        variant: "destructive"
      });
    } finally {
      setRemoving(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Manage Administrators</h1>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Add or remove admin access for users
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
            style={{ borderRadius: 0 }}
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Add New Admin - Email Lookup */}
            <div className="lg:col-span-1">
              <div 
                className="p-6"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-light)' 
                }}
              >
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <UserPlus size={20} style={{ color: 'var(--olive-primary)' }} />
                  Add New Admin
                </h2>
                
                <form onSubmit={handleEmailLookup} className="space-y-4">
                  <div>
                    <Label htmlFor="email">User Email Address</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="email"
                        type="email"
                        value={searchEmail}
                        onChange={(e) => {
                          setSearchEmail(e.target.value);
                          setSearchError('');
                          setFoundUser(null);
                        }}
                        placeholder="user@example.com"
                        style={{ borderRadius: 0 }}
                      />
                      <Button 
                        type="submit" 
                        variant="outline"
                        disabled={searching || !searchEmail.trim()}
                        style={{ borderRadius: 0 }}
                      >
                        {searching ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <Search size={16} />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                      Enter the email of a user who has signed in to look them up
                    </p>
                  </div>
                </form>

                {/* Search Error */}
                {searchError && (
                  <div 
                    className="mt-4 p-3 text-sm"
                    style={{ 
                      backgroundColor: '#fef2f2', 
                      border: '1px solid #fecaca',
                      color: '#dc2626'
                    }}
                  >
                    {searchError}
                  </div>
                )}

                {/* Found User Card */}
                {foundUser && (
                  <div 
                    className="mt-4 p-4"
                    style={{ 
                      backgroundColor: 'var(--bg-primary)', 
                      border: '2px solid var(--olive-primary)' 
                    }}
                  >
                    <h3 className="heading-3 mb-3 flex items-center gap-2">
                      <User size={18} />
                      User Found
                    </h3>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2">
                        <Mail size={14} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small">{foundUser.email}</span>
                      </div>
                      {foundUser.full_name && (
                        <div className="flex items-center gap-2">
                          <User size={14} style={{ color: 'var(--text-secondary)' }} />
                          <span className="body-small">{foundUser.full_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar size={14} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small">Signed up: {formatDate(foundUser.created_at)}</span>
                      </div>
                      {foundUser.last_sign_in && (
                        <div className="flex items-center gap-2">
                          <Calendar size={14} style={{ color: 'var(--text-secondary)' }} />
                          <span className="body-small">Last login: {formatDate(foundUser.last_sign_in)}</span>
                        </div>
                      )}
                    </div>

                    {foundUser.is_admin ? (
                      <div 
                        className="p-3 flex items-center gap-2"
                        style={{ backgroundColor: 'var(--bg-olive-light)' }}
                      >
                        <Shield size={16} style={{ color: 'var(--olive-primary)' }} />
                        <span className="body-small" style={{ color: 'var(--olive-primary)' }}>
                          Already an administrator
                        </span>
                      </div>
                    ) : (
                      <Button 
                        onClick={handleElevateToAdmin}
                        className="btn-primary w-full"
                        disabled={adding}
                      >
                        {adding ? (
                          <>
                            <Loader2 className="animate-spin mr-2" size={16} />
                            Elevating...
                          </>
                        ) : (
                          <>
                            <Shield size={16} className="mr-2" />
                            Elevate to Administrator
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Your Info */}
              <div 
                className="p-6 mt-6"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-light)' 
                }}
              >
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Shield size={20} style={{ color: 'var(--olive-primary)' }} />
                  Your Info
                </h2>
                
                {currentUserInfo && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                        Email
                      </Label>
                      <p className="body-regular">{currentUserInfo.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                        Status
                      </Label>
                      <p className="body-regular flex items-center gap-2">
                        <Shield size={16} style={{ color: 'var(--olive-primary)' }} />
                        <span style={{ color: 'var(--olive-primary)' }}>Administrator</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Admin List */}
            <div className="lg:col-span-2">
              <div 
                className="p-6"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-light)' 
                }}
              >
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Users size={20} style={{ color: 'var(--olive-primary)' }} />
                  Current Administrators ({admins.length})
                </h2>
                
                {admins.length === 0 ? (
                  <p className="body-regular py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    No administrators found.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {admins.map((admin) => (
                      <div 
                        key={admin.id || admin.user_id}
                        className="p-4 flex items-center justify-between"
                        style={{ 
                          backgroundColor: 'var(--bg-primary)', 
                          border: '1px solid var(--border-light)' 
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="p-2"
                            style={{ 
                              backgroundColor: 'var(--olive-primary)', 
                              color: 'white' 
                            }}
                          >
                            <Shield size={20} />
                          </div>
                          <div>
                            <p className="body-regular font-medium">
                              {admin.email || 'Admin User'}
                            </p>
                            <code 
                              className="text-xs"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              {admin.user_id}
                            </code>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyToClipboard(admin.user_id)}
                            title="Copy User ID"
                            style={{ borderRadius: 0 }}
                          >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                          </Button>
                          {currentUserInfo?.user_id !== admin.user_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveAdmin(admin.user_id, admin.email)}
                              disabled={removing === admin.user_id}
                              style={{ 
                                borderRadius: 0, 
                                borderColor: '#ef4444', 
                                color: '#ef4444' 
                              }}
                            >
                              {removing === admin.user_id ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <>
                                  <ShieldOff size={16} className="mr-1" />
                                  Remove
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div 
                className="p-6 mt-6"
                style={{ 
                  backgroundColor: 'var(--bg-olive-light)', 
                  border: '1px solid var(--border-light)' 
                }}
              >
                <h3 className="heading-3 mb-3">How to Add a New Admin</h3>
                <ol className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                  <li>1. Ask the user to sign in with their Google account on the website</li>
                  <li>2. Enter their email address in the search box above</li>
                  <li>3. Review their details to confirm it's the right person</li>
                  <li>4. Click "Elevate to Administrator" to grant admin access</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
